import type { LoginCredentials, LoginResponse, User, UserRole, WatchmanLoginResponse } from "../type";
import { getOspreyApiBaseUrl } from "../../../shared/utils/apiBase";
import type { TwoFactorEnrollment } from "./twoFactorApiClient";

// Admin login goes through osprey-api's iam-proxy (<base>/iam/auth/login), which
// forwards to internal watchman (incl. /auth/me below). The Osprey UI never calls
// watchman directly — single origin (osprey-api). See apiBase.ts.
const getWatchmanUrl = (): string => getOspreyApiBaseUrl() + '/iam';

function decodeJwtPayload(token: string): Record<string, unknown> {
  const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
      .join('')
  );
  return JSON.parse(json);
}

// Given a fresh watchman access token, build the app User. The JWT only carries
// { sub, roleName, roleScope, tenantId, merchantId, p }; email/fullName/permissions and
// twoFactorEnabled come from /auth/me (best-effort — falls back to token claims). Shared
// by login, the 2FA legs and the forced-password-update completion so all four produce an
// identically-hydrated session.
async function hydrateSession(accessToken: string): Promise<LoginResponse> {
  const url = getWatchmanUrl();
  const payload = decodeJwtPayload(accessToken);

  let me: Record<string, unknown> = {};
  try {
    const meRes = await fetch(`${url}/auth/me`, {
      headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' },
    });
    if (meRes.ok) me = await meRes.json();
  } catch (err) {
    // best-effort: /auth/me only enriches the session (email/fullName/permissions);
    // on failure we fall back to the JWT claims below so login still succeeds. Log
    // so a silent enrichment gap is still diagnosable in the field.
    console.warn('[auth] hydrateSession: /auth/me enrichment failed, using token claims', err);
  }

  const user: User = {
    id: (me.userId as string) ?? (payload.sub as string),
    email: (me.email as string) ?? '',
    fullName: (me.fullName as string) ?? '',
    roleName: (me.roleName as UserRole) ?? (payload.roleName as UserRole),
    roleScope: (me.roleScope as string) ?? (payload.roleScope as string),
    merchantId:
      ((me.merchantId ?? me.tenantId ?? payload.tenantId ?? payload.merchantId) as string | null) ?? null,
    isProfileCompleted: (me.isProfileCompleted as boolean) ?? false,
    permissions: (me.permissions as Record<string, string[]>) ?? {},
  };

  // Only /auth/me reports 2FA status; the JWT has no equivalent claim to fall
  // back on. So a failed enrichment leaves it null (unknown) rather than false,
  // which would read as a definite "2FA is off" the server never said.
  const twoFactorEnabled = typeof me.twoFactorEnabled === 'boolean' ? me.twoFactorEnabled : null;

  return { token: accessToken, user, twoFactorEnabled };
}

/**
 * Shapes a failed watchman response into the throw contract the auth screens
 * branch on. watchman sends `message` as a string for domain errors but as a
 * string[] for class-validator failures, so the array case has to be unwrapped
 * or the UI renders "[object Object]".
 */
async function toAuthError(res: Response, fallback: string) {
  const err = await res.json().catch(() => ({}));
  const rawMsg = err.message;
  const message = Array.isArray(rawMsg)
    ? (rawMsg[0] || fallback)
    : (rawMsg || err.errors?.[0]?.message || res.statusText || fallback);
  return { status: res.status, code: err.code, message, errors: err.errors, fullError: err };
}

/** Thrown by login when the account still holds a watchman-issued temporary password. */
export const PASSWORD_UPDATE_REQUIRED = 'PASSWORD_UPDATE_REQUIRED';

// 2FA codes (PE-2157) live in twoFactorApiClient so the challenge screen and the
// self-service screen share one definition; re-exported here because the login
// form branches on them right next to PASSWORD_UPDATE_REQUIRED.
export {
  TWO_FACTOR_REQUIRED,
  TWO_FACTOR_ENROLLMENT_REQUIRED,
} from './twoFactorApiClient';

export const authApiClient = {
  login: async (data: LoginCredentials): Promise<LoginResponse> => {
    const url = getWatchmanUrl();
    const res = await fetch(`${url}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ email: data.email, password: data.password }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const message = err.message || err.errors?.[0]?.message || res.statusText || 'Login failed';
      // Surface the machine-readable code (e.g. PASSWORD_UPDATE_REQUIRED) so the caller can
      // branch to the forced-password-update screen instead of showing a generic error.
      // TWO_FACTOR_REQUIRED additionally carries `pendingToken`, which the 2FA challenge
      // needs — it exists only on this response body, so it has to ride along here.
      throw {
        status: res.status,
        code: err.code,
        pendingToken: err.pendingToken,
        message,
        errors: err.errors,
        fullError: err,
      };
    }

    const { accessToken }: WatchmanLoginResponse = await res.json();
    return hydrateSession(accessToken);
  },

  // PE-1667 / PE-2097 — complete a forced password update after login returned
  // PASSWORD_UPDATE_REQUIRED. Verifies the current (temporary) password and sets a new one,
  // then watchman issues a fresh token pair so the caller proceeds straight to a session.
  completePasswordUpdate: async (data: {
    email: string;
    oldPassword: string;
    newPassword: string;
  }): Promise<LoginResponse> => {
    const url = getWatchmanUrl();
    const res = await fetch(`${url}/auth/complete-password-update`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(data),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const rawMsg = err.message;
      const message = Array.isArray(rawMsg)
        ? (rawMsg[0] || 'Could not update password')
        : (rawMsg || err.errors?.[0]?.message || res.statusText || 'Could not update password');
      throw { status: res.status, code: err.code, message, errors: err.errors, fullError: err };
    }

    const { accessToken }: WatchmanLoginResponse = await res.json();
    return hydrateSession(accessToken);
  },

  /**
   * Second leg of a 2FA login (PE-2157). `login` above throws with
   * `code: TWO_FACTOR_REQUIRED` and a `pendingToken`; this exchanges that token
   * plus a TOTP or backup code for a real session.
   *
   * The pending session is single-use and expires 5 minutes after the original
   * password check, so the caller keeps `pendingToken` in component state only —
   * it is a bearer-equivalent credential and must never be persisted or put in
   * the URL. A 401 here is "wrong code", not "session gone", unless the message
   * says the two-factor session expired; the form distinguishes the two so a
   * typo doesn't send the user back to re-enter their password.
   */
  verifyTwoFactor: async (pendingToken: string, code: string): Promise<LoginResponse> => {
    const url = getWatchmanUrl();
    const res = await fetch(`${url}/auth/2fa/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ pendingToken, code }),
    });

    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      const rawMsg = err.message;
      const message = Array.isArray(rawMsg)
        ? (rawMsg[0] || 'That code was not accepted')
        : (rawMsg || err.errors?.[0]?.message || res.statusText || 'That code was not accepted');
      throw { status: res.status, code: err.code, message, errors: err.errors, fullError: err };
    }

    const { accessToken }: WatchmanLoginResponse = await res.json();
    return hydrateSession(accessToken);
  },

  /**
   * Bootstrap enrolment, leg 1 (PE-2157, backend watchman #102).
   *
   * The signed-out counterpart to `twoFactorApiClient.enroll()`. `login` throws
   * with `code: TWO_FACTOR_ENROLLMENT_REQUIRED` when 2FA is mandatory for this
   * account's role/merchant policy but was never set up; that response now
   * carries a `pendingToken`, and this exchanges it for a TOTP secret + QR.
   *
   * ⚠️ Note the route: `/auth/2fa/enroll`, NOT the `/2fa/enroll` that
   * twoFactorApiClient calls. They are different endpoints on different watchman
   * controllers — this one is `@Public()` and authenticates via the pendingToken,
   * the other sits behind the global AuthGuard and needs a Bearer token. Sending
   * a pendingToken to `/2fa/enroll` gets a 401, which is exactly the deadlock
   * this pair exists to break.
   *
   * The secret is held server-side and is NOT active until
   * `completeEnrollment` succeeds, so abandoning the screen leaves 2FA off and
   * the account still locked out — no partial state to clean up.
   */
  startEnrollment: async (pendingToken: string): Promise<TwoFactorEnrollment> => {
    const url = getWatchmanUrl();
    const res = await fetch(`${url}/auth/2fa/enroll`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ pendingToken }),
    });

    if (!res.ok) throw await toAuthError(res, 'Could not start two-step setup');
    return res.json();
  },

  /**
   * Bootstrap enrolment, leg 2 (PE-2157, backend watchman #102).
   *
   * Verifies the first authenticator code against the secret from
   * `startEnrollment`, activates 2FA, and — because this account had no session
   * to begin with — issues a real token pair in the SAME call, alongside the ten
   * single-use backup codes.
   *
   * That combination is why the caller must not navigate on success: the backup
   * codes are shown exactly once and the server will never repeat them, so they
   * have to be surfaced and acknowledged before the session is consumed. Unlike
   * `verifyTwoFactor`, a failure here is not just a retry — a lapsed pendingToken
   * means restarting from the password step.
   */
  completeEnrollment: async (
    pendingToken: string,
    code: string,
  ): Promise<LoginResponse & { backupCodes: string[] }> => {
    const url = getWatchmanUrl();
    const res = await fetch(`${url}/auth/2fa/enroll/confirm`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ pendingToken, code }),
    });

    if (!res.ok) throw await toAuthError(res, 'That code was not accepted');

    const {
      accessToken,
      backupCodes,
    }: WatchmanLoginResponse & { backupCodes?: string[] } = await res.json();
    const session = await hydrateSession(accessToken);
    // Default to [] rather than throwing: the session is already valid at this
    // point, so a missing codes array must not cost the user their login.
    return { ...session, backupCodes: backupCodes ?? [] };
  },
};
